<template>
  <div class="page-shell">
    <div class="topbar">
      <div class="page-title"><h1>🎨 Banner管理</h1><p>管理首页Hero区域的内容展示</p></div>
    </div>

    <div class="card">
      <div class="card-header"><h3>Banner 预览</h3></div>
      <div class="banner-preview banner-preview-clickable" @click="openImageDialog">
        <div class="banner-img-wrap">
          <img v-if="form.image" :src="form.image" alt="Banner图片" referrerpolicy="no-referrer">
          <div v-else-if="loading" class="banner-placeholder">
            <p>Banner 加载中...</p>
          </div>
          <div v-else class="banner-placeholder">
            <h2 v-html="form.title"></h2>
            <p>{{ form.subtitle }}</p>
          </div>
        </div>
        <div class="banner-edit-hint">点击预览框更换图片</div>
      </div>
    </div>

    <div class="card">
      <div class="card-header"><h3>编辑 Banner 内容</h3><button class="btn btn-primary" :disabled="loading || saving || uploading" @click="save()">{{ saving ? '保存中...' : '💾 保存修改' }}</button></div>
      <div class="two-col-grid">
        <div class="form-group"><label>大标题（支持HTML标签如&lt;span&gt;）</label><input v-model="form.title" type="text"></div>
        <div class="form-group"><label>副标题</label><input v-model="form.subtitle" type="text"></div>
      </div>
    </div>

    <el-dialog title="更换 Banner 图片" :visible.sync="imageDialog" width="560px">
      <div class="form-group">
        <label>图片 URL（外链）</label>
        <input v-model="imageDraft" type="text" placeholder="请输入图片外链地址">
      </div>
      <div class="form-group">
        <label>上传图片</label>
        <input ref="imageInput" type="file" accept="image/*" @change="handleFile">
        <div v-if="uploading" class="upload-hint">图片上传中...</div>
      </div>
      <div class="dialog-preview">
        <img v-if="imageDraft" :src="imageDraft" alt="Banner预览" referrerpolicy="no-referrer">
        <div v-else class="dialog-preview__empty">暂无图片，确认后将展示文案占位效果</div>
      </div>
      <div slot="footer">
        <el-button @click="clearImage">清空图片</el-button>
        <el-button @click="imageDialog = false">取消</el-button>
        <el-button type="primary" :loading="saving" :disabled="uploading" @click="applyImage">确认并保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { bannerCreate, bannerDetail, bannerUpdate, bannerUpload } from '../../api/userMG'

const BANNER_ID = 1

const emptyForm = () => ({
  image: '',
  title: '',
  subtitle: '',
  status: 1,
  sort: 0
})

export default {
  name: 'home-banner',
  data() {
    return {
      form: emptyForm(),
      imageDialog: false,
      imageDraft: '',
      loading: false,
      saving: false,
      uploading: false,
      bannerExists: false,
      bannerMissing: false
    }
  },
  created() {
    this.loadBanner()
  },
  methods: {
    async loadBanner() {
      this.loading = true

      try {
        const res = await bannerDetail(BANNER_ID)
        const detail = this.extractDetail(res)

        if (detail) {
          this.bannerExists = true
          this.bannerMissing = false
          this.form = this.normalizeBanner(detail)
          return
        }

        this.bannerExists = false
        this.bannerMissing = this.isNotFoundResponse(res)
        this.form = emptyForm()

        if (!this.bannerMissing && this.isRequestFailed(res)) {
          this.$message.error(this.getErrorMessage(res, 'Banner内容加载失败'))
        }
      } catch (error) {
        this.bannerExists = false
        this.bannerMissing = false
        this.form = emptyForm()
        this.$message.error(this.getErrorMessage(error, 'Banner内容加载失败'))
      } finally {
        this.loading = false
      }
    },
    openImageDialog() {
      this.imageDraft = this.form.image || ''
      this.imageDialog = true
      this.$nextTick(() => {
        if (this.$refs.imageInput) {
          this.$refs.imageInput.value = ''
        }
      })
    },
    async handleFile(event) {
      const file = event.target.files[0]
      if (!file) return

      const formData = new FormData()
      formData.append('file', file)
      this.uploading = true

      try {
        const res = await bannerUpload(formData)

        if (this.isMutationFailed(res)) {
          this.$message.error(this.getErrorMessage(res, '图片上传失败'))
          return
        }

        const imageUrl = this.extractUploadUrl(res)

        if (!imageUrl) {
          this.$message.error('上传成功，但未获取到图片地址')
          return
        }

        this.imageDraft = imageUrl
        this.$message.success('图片上传成功')
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '图片上传失败'))
      } finally {
        this.uploading = false
        event.target.value = ''
      }
    },
    clearImage() {
      this.imageDraft = ''
    },
    applyImage() {
      this.form.image = (this.imageDraft || '').trim()
      this.save('Banner图片已更新')
    },
    async save(message) {
      if (this.loading || this.saving || this.uploading) return

      const successMessage = typeof message === 'string' ? message : 'Banner内容已更新！'

      if (!this.bannerExists && !this.bannerMissing) {
        this.$message.error('Banner详情加载失败，暂不执行保存，请稍后重试')
        return
      }

      this.saving = true

      try {
        const payload = this.buildPayload()
        const res = this.bannerExists
          ? await bannerUpdate(BANNER_ID, payload)
          : await bannerCreate(payload)

        if (this.isMutationFailed(res)) {
          this.$message.error(this.getErrorMessage(res, this.bannerExists ? 'Banner更新失败' : 'Banner创建失败'))
          return
        }

        this.imageDialog = false
        this.$message.success(successMessage)
        await this.loadBanner()
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, this.bannerExists ? 'Banner更新失败' : 'Banner创建失败'))
      } finally {
        this.saving = false
      }
    },
    normalizeBanner(detail) {
      return {
        image: detail.image_url || detail.image || '',
        title: detail.title || '',
        subtitle: detail.subtitle || '',
        status: detail.status === undefined || detail.status === null ? 1 : Number(detail.status),
        sort: detail.sort === undefined || detail.sort === null ? 0 : Number(detail.sort)
      }
    },
    buildPayload() {
      return {
        image_url: (this.form.image || '').trim(),
        title: (this.form.title || '').trim(),
        subtitle: (this.form.subtitle || '').trim(),
        status: this.form.status === undefined || this.form.status === null ? 1 : Number(this.form.status),
        sort: this.form.sort === undefined || this.form.sort === null ? 0 : Number(this.form.sort)
      }
    },
    extractDetail(res) {
      if (!res || typeof res !== 'object' || res.detail || res.errors) {
        return null
      }

      if (res.data && !Array.isArray(res.data) && typeof res.data === 'object') {
        return res.data
      }

      if (res.message) {
        return null
      }

      return res
    },
    extractUploadUrl(res) {
      const payload = this.extractDetail(res)

      if (!payload || typeof payload !== 'object') {
        return ''
      }

      return payload.url || payload.image_url || payload.path || payload.file_url || ''
    },
    isRequestFailed(res) {
      return typeof res === 'string' || !!(res && typeof res === 'object' && (res.message || res.detail || res.errors))
    },
    isMutationFailed(res) {
      return typeof res === 'string' || !!(res && typeof res === 'object' && (res.detail || res.errors))
    },
    isNotFoundResponse(res) {
      if (!this.isRequestFailed(res)) return false

      const message = this.getErrorMessage(res, '').toLowerCase()
      return message.includes('404') || message.includes('not found') || message.includes('不存在') || message.includes('no query results')
    },
    getErrorMessage(error, fallback) {
      if (!error) return fallback
      if (typeof error === 'string') return error
      if (error.message) return error.message
      if (error.detail) return error.detail
      if (error.errors) {
        const firstKey = Object.keys(error.errors)[0]
        const firstError = firstKey ? error.errors[firstKey] : ''

        if (Array.isArray(firstError)) {
          return firstError[0] || fallback
        }

        return firstError || fallback
      }

      return fallback
    }
  }
}
</script>

<style scoped>
.banner-preview-clickable {
  cursor: pointer;
}

.banner-preview-clickable:hover {
  box-shadow: var(--cog-shadow);
}

.banner-edit-hint {
  position: absolute;
  right: 16px;
  bottom: 14px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.62);
  color: #fff;
  font-size: 12px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
}

.form-group input {
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  border: 1.5px solid var(--cog-border);
  border-radius: var(--cog-radius-sm);
}

.upload-hint {
  margin-top: 8px;
  font-size: 12px;
  color: var(--cog-text-secondary);
}

.dialog-preview {
  margin-top: 16px;
  min-height: 180px;
  border: 1px dashed var(--cog-border);
  border-radius: var(--cog-radius);
  overflow: hidden;
  background: #f8fafc;
}

.dialog-preview img {
  display: block;
  width: 100%;
  height: 240px;
  object-fit: cover;
}

.dialog-preview__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  padding: 16px;
  color: var(--cog-text-secondary);
  font-size: 13px;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/views/home/banner.vue